import { memo, useCallback, type ChangeEvent, type ReactNode } from 'react';
import { PrivacyToggle } from '../PrivacyToggle/PrivacyToggle';
import type { PrivacyScope } from '../../../context/PrivacyContext';

interface SearchConfig {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  name?: string;
  type?: 'search' | 'text';
}

interface FilterBarProps {
  /**
   * Page whose hide mode this bar's eye governs. Given one, the bar renders the
   * eye beside its refresh control — the four list pages all reach hide mode
   * through here.
   */
  privacyScope?: PrivacyScope;
  search?: SearchConfig;
  controls?: ReactNode;
  onRefresh?: () => void;
  refreshAriaLabel?: string;
  rightAction?: ReactNode;
}

export const FilterBar = memo(function FilterBar({
  search,
  controls,
  privacyScope,
  onRefresh,
  refreshAriaLabel = 'Refresh',
  rightAction,
}: FilterBarProps) {
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      search?.onChange(e.target.value);
    },
    [search],
  );

  return (
    <div className="filter-bar">
      <div className="filter-bar-inner">
        <div className="filter-controls">
          {search && (
            <div className="search-wrapper">
              <span className="search-icon" aria-hidden="true">
                <span className="material-symbols-outlined">search</span>
              </span>
              <input
                type={search.type ?? 'search'}
                name={search.name}
                className="search-input"
                placeholder={search.placeholder}
                value={search.value}
                onChange={handleSearchChange}
                aria-label={search.ariaLabel ?? search.placeholder}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
          )}

          {controls}

          {privacyScope && <PrivacyToggle scope={privacyScope} />}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="refresh-btn"
              aria-label={refreshAriaLabel}
            >
              <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
            </button>
          )}
        </div>

        {rightAction}
      </div>
    </div>
  );
});

export default FilterBar;
